import {APP_CONFIG} from "../../config/GeneralConfig";
import {ErrorAction, ReduxAction} from "../../models/common/ActionTypes";
import {Person} from "../../models/common/Person";
import {Observable, of, timeout} from "rxjs";
import {ofType, StateObservable} from "redux-observable";
import {AppState} from "../../reducers/RootReducer";
import {catchError, switchMap} from "rxjs/operators";
import {RouteMethods} from "../../config/RouteConfig";
import {ajax} from "rxjs/ajax";
import {eventNotificationError} from "../common/EventNotificationActions";

/**
 * base API path to interact with project roles
 */
function apiPath(): string {
    return APP_CONFIG.baseWeb + "tenants/register/";
}

/**
 * Represents an action to register a new tenant in the system.
 * This action is used to capture and transmit the necessary details for registering a tenant.
 *
 * This interface extends the base ReduxAction to include specific data
 * required for tenant registration, such as personal information, company details,
 * and authentication credentials. It is typically dispatched as part of the user
 * onboarding workflow.
 *
 * @extends ReduxAction<typeof REGISTER_TENANT>
 *
 * @param person - An object containing the personal details of the tenant. This may include their name,
 *                 contact information, and other relevant personal attributes.
 * @param companyName - The name of the company associated with the tenant. This is assumed to be a non-empty string
 *                      and cannot include invalid characters.
 * @param password - A string representing the tenant's password. It is expected to be securely handled and
 *                   meet minimum security requirements (e.g., length, character complexity).
 */
export interface RegisterTenantAction extends ReduxAction<typeof REGISTER_TENANT> {
    person: Person
    email: string
    password: string
}

/**
 * Represents an action triggered when a tenant registration process completes successfully.
 *
 * This action is used to notify the application state that the tenant registration workflow has
 * finished without errors, allowing the system to transition to the appropriate state or execute
 * any post-registration logic.
 *
 * @extends ReduxAction<typeof REGISTER_TENANT_SUCCESS>
 * Indicates that this interface extends the base `ReduxAction` type, specifically for the
 * `REGISTER_TENANT_SUCCESS` identifier.
 */
export interface RegisterTenantSuccessAction extends ReduxAction<typeof REGISTER_TENANT_SUCCESS> {

}

/**
 * Represents an error occurring during the registration of a tenant.
 *
 * This interface is used to encapsulate information about errors that may occur
 * when attempting to register a tenant in the system, ensuring consistent error handling.
 *
 * Developers can leverage this interface to identify the specific error and
 * take appropriate action, such as logging or retries.
 *
 * @extends ErrorAction<typeof REGISTER_TENANT_ERROR>
 * Extends the base `ErrorAction` interface, providing additional specificity for tenant registration errors.
 */
export interface RegisterTenantErrorAction extends ErrorAction< typeof REGISTER_TENANT_ERROR> {

}

/**
 * Represents the action type for registering a new tenant in the system.
 *
 * This constant is used as an identifier for triggering tenant registration-related actions
 * across the application. It enables consistent communication between various parts of the code.
 *
 * Developers should use this constant to ensure that tenant registration actions are handled
 * uniformly. Avoid hardcoding the action type to maintain scalability and reduce errors.
 */
export const REGISTER_TENANT = "REGISTER_TENANT";
/**
 * Represents the unique identifier for the successful tenant registration action.
 *
 * This constant is used as an action type in state management (e.g., Redux) to signal that a tenant registration process
 * has completed successfully. It helps differentiate this specific action from other actions within the application.
 *
 * Developers should use this constant to dispatch or handle events related to the successful registration of a tenant.
 *
 * Constraints:
 * - Must be unique within the application's action type namespace to prevent unintended behavior.
 * - Should only be associated with processes that confirm a successful tenant registration.
 */
export const REGISTER_TENANT_SUCCESS = "REGISTER_TENANT_SUCCESS";
/**
 * A constant representing the error code for tenant registration failures.
 *
 * This error code is used to indicate that there was a problem during the
 * registration process of a tenant. It acts as a standardized identifier for
 * this specific type of error, enabling developers to handle and classify
 * errors consistently across the system.
 *
 * Key considerations:
 * - This value is intended to be immutable and used as a recognizable error
 *   indicator in logs, debugging, and error handling mechanisms.
 * - Ensure this error code is properly mapped and described in user-facing
 *   documentation, if exposed externally.
 */
export const REGISTER_TENANT_ERROR = "REGISTER_TENANT_ERROR";

/**
 * Registers a new tenant in the system.
 *
 * This function creates an action object to initiate the process of registering a tenant
 * with the provided person details, company name, and secure password. It is typically used
 * to dispatch the registration action in a state management system.
 *
 * The registration ensures the association of a person with a company under a unique account,
 * secured by the provided password.
 *
 * @param person - An object containing details about the person being registered. It is expected
 *                 to adhere to the `Person` interface structure.
 * @param email - The name of the company the tenant is associated with. Must be a non-empty string.
 * @param password - A password to secure the tenant's account. Recommended to follow strong password policies.
 * @return An action object of type `RegisterTenantAction` containing the necessary registration details.
 * @throws Will not handle validation or errors directly, external validation is suggested before invoking this function.
 */
export function registerTenant(
    person: Person,
    email: string,
    password: string
) : RegisterTenantAction {
    return { type: REGISTER_TENANT, person, email, password };
}

/**
 * Indicates the successful registration of a tenant.
 *
 * This function creates and returns an action object that signifies
 * the successful completion of a tenant registration process. It is
 * used to update the application's state to reflect this success,
 * typically in a Redux-based state management setup.
 *
 * @return An action object with the type `REGISTER_TENANT_SUCCESS`.
 */
export function registerTenantSuccess() : RegisterTenantSuccessAction {
    return { type: REGISTER_TENANT_SUCCESS };
}

/**
 * Creates an action to record an error that occurred during the tenant registration process.
 *
 * This function is used to handle and track errors when a tenant registration operation fails,
 * ensuring the error can be properly processed and recorded in the application's state.
 *
 * @param error - The error object or message describing the issue that occurred during tenant registration.
 * @return An action object containing the type `REGISTER_TENANT_ERROR` and the provided error information.
 */
export function registerTenantError( error: any ) : RegisterTenantErrorAction {
    return { type: REGISTER_TENANT_ERROR, error };
}

/**
 * Handles the registration process for a new tenant within the application.
 *
 * This method observes tenant registration actions, processes API calls to
 * register the tenant, and handles success or failure responses accordingly.
 * It is responsible for ensuring that valid tenant data is sent to the server
 * and appropriately handled in the application state upon response.
 *
 * @param action$ - The stream of dispatched `RegisterTenantAction` actions.
 *                  This is observed to detect when tenant registration is triggered.
 * @param store$ - The application's state observable, used to access the current
 *                 state during the process and to pass it for error notifications.
 * @return The processed observable stream that emits either a success action
 *         (`registerTenantSuccess`) or an error action (`registerTenantError`).
 * @throws Will trigger an error notification (`eventNotificationError`) if the
 *         API call fails, logging relevant error details and state context for debugging.
 */
export function registerTenantEpic(
    action$: Observable<RegisterTenantAction>,
    store$: StateObservable<AppState>
): any {
    return action$.pipe(
        ofType( REGISTER_TENANT ),
        switchMap( action =>
           ajax(
                APP_CONFIG.getRequestObject(
                    apiPath(),
                    RouteMethods.post,
                    {
                        person: action.person,
                        email: action.email,
                        password: action.password
                    }
                )
            ).pipe(
                timeout(300000),
                switchMap( response => of(
                    registerTenantSuccess()
                )),
                catchError(error => of(
                    registerTenantError(error),
                    eventNotificationError(error, store$, "TenantRegister", "registerTenant")
                ))
            )
        )
    );
}